import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type { ChoreDefinition } from '@chore-project/shared';
import { ChoreLength, ChorePriority, Recurrence } from '@chore-project/shared';

interface CreateChoreDefinitionInput {
  name: string;
  description?: string;
  recurrence: Recurrence;
  dayOfWeek?: number;
  dayOfMonth?: number;
  defaultAssigneeId?: string;
  roomId?: string;
  length?: ChoreLength;
  priority?: ChorePriority;
}

/** Fetch chore definitions for multiple households and merge into a single array. */
export function useAllChoreDefinitions(householdIds: string[]) {
  const results = useQueries({
    queries: householdIds.map((id) => ({
      queryKey: ['chore-definitions', id],
      queryFn: async () => {
        const { data } = await apiClient.get<ChoreDefinition[]>(
          `/households/${id}/chore-definitions`,
        );
        return data;
      },
      enabled: !!id,
    })),
  });
  const defs = results.flatMap((r) => r.data ?? []);
  const isLoading = results.some((r) => r.isLoading);
  return { data: defs, isLoading };
}

export function useChoreDefinitions(householdId: string) {
  return useQuery<ChoreDefinition[]>({
    queryKey: ['chore-definitions', householdId],
    queryFn: async () => {
      const { data } = await apiClient.get<ChoreDefinition[]>(
        `/households/${householdId}/chore-definitions`,
      );
      return data;
    },
    enabled: !!householdId,
  });
}

export function useCreateChoreDefinition(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<ChoreDefinition, Error, CreateChoreDefinitionInput>({
    mutationFn: async (dto) => {
      const { data } = await apiClient.post<ChoreDefinition>(
        `/households/${householdId}/chore-definitions`,
        dto,
      );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['chore-definitions', householdId] }),
  });
}

export function useUpdateChoreDefinition(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<ChoreDefinition, Error, { choreDefId: string } & Partial<CreateChoreDefinitionInput>>({
    mutationFn: async ({ choreDefId, ...dto }) => {
      const { data } = await apiClient.patch<ChoreDefinition>(
        `/households/${householdId}/chore-definitions/${choreDefId}`,
        dto,
      );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['chore-definitions', householdId] }),
  });
}

export function useDeleteChoreDefinition(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (choreDefId) => {
      await apiClient.delete(`/households/${householdId}/chore-definitions/${choreDefId}`);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['chore-definitions', householdId] }),
  });
}
